import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Shield, QrCode, Key, AlertTriangle, Check, X } from "lucide-react";

export default function TwoFactorPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const response = await api.getMe();
      setUser(response.data);
      if (!response.data.two_factor_enabled) {
        setShowSetup(true);
      }
    } catch (error) {
      toast.error("Error al cargar usuario");
    }
  };

  const setup2FA = async () => {
    setLoading(true);
    try {
      const response = await api.setup2FA();
      setSetupData(response.data);
      setBackupCodes(response.data.backup_codes);
      toast.success("2FA configurado. Escanea el QR code");
    } catch (error) {
      toast.error("Error al configurar 2FA");
    } finally {
      setLoading(false);
    }
  };

  const enable2FA = async () => {
    if (!verifyCode) {
      toast.error("Ingresa el código de verificación");
      return;
    }

    setLoading(true);
    try {
      await api.enable2FA({ code: verifyCode });
      toast.success("2FA habilitado correctamente");
      setShowSetup(false);
      loadUser();
    } catch (error) {
      toast.error("Código de verificación inválido");
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode) {
      toast.error("Ingresa el código de verificación");
      return;
    }

    setLoading(true);
    try {
      await api.disable2FA({ code: disableCode });
      toast.success("2FA deshabilitado correctamente");
      setShowDisable(false);
      loadUser();
    } catch (error) {
      toast.error("Código de verificación inválido");
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Códigos de backup descargados");
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <Shield className="w-8 h-8 text-blue-600" />
            Autenticación de Dos Factores
          </h1>
          <p className="mt-2 text-gray-600">
            Protege tu cuenta con un código de seguridad adicional
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Estado Actual */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Estado de Seguridad
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center">
                {user.two_factor_enabled ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <Check className="w-6 h-6" />
                      <span className="text-lg font-semibold">2FA Habilitado</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Tu cuenta está protegida con autenticación de dos factores.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowDisable(true)}
                      className="w-full"
                    >
                      Deshabilitar 2FA
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-yellow-600">
                      <AlertTriangle className="w-6 h-6" />
                      <span className="text-lg font-semibold">2FA Deshabilitado</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Tu cuenta no está protegida. Habilita 2FA para mayor seguridad.
                    </p>
                    <Button 
                      onClick={setup2FA}
                      disabled={loading}
                      className="w-full"
                    >
                      {loading ? "Configurando..." : "Habilitar 2FA"}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Configuración */}
          {showSetup && setupData && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5" />
                    Configurar 2FA
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <img 
                      src={`data:image/png;base64,${setupData.qr_code}`}
                      alt="QR Code para 2FA"
                      className="mx-auto border-2 border-gray-200 rounded-lg p-2"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Código de verificación</label>
                    <Input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-lg tracking-widest"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Códigos de Backup</label>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-2">
                        Guarda estos códigos en un lugar seguro. Solo puedes usarlos una vez.
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                        {backupCodes.map((code, index) => (
                          <div key={index} className="bg-white p-2 rounded border">
                            {code}
                          </div>
                        ))}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={downloadBackupCodes}
                        className="w-full mt-2"
                      >
                        Descargar Códigos
                      </Button>
                    </div>
                  </div>

                  <Button 
                    onClick={enable2FA}
                    disabled={loading || !verifyCode}
                    className="w-full"
                  >
                    {loading ? "Verificando..." : "Habilitar 2FA"}
                  </Button>

                  <div className="text-xs text-gray-600 space-y-1">
                    <p>1. Escanea el QR code con Google Authenticator o Authy</p>
                    <p>2. Ingresa el código de 6 dígitos</p>
                    <p>3. Guarda los códigos de backup</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Modal para deshabilitar */}
        {showDisable && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Deshabilitar 2FA</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Ingresa el código de tu aplicación autenticadora para deshabilitar 2FA.
                </p>
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDisable(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={disable2FA}
                    disabled={loading || !disableCode}
                    className="flex-1"
                  >
                    {loading ? "Verificando..." : "Deshabilitar"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
